/**
 * Hook for fetching full content data by content key
 */

import { useState, useEffect, useCallback, useRef } from "react";
import { fetchContentByKey } from "@/api/content";

interface UseContentByKeyResult<T> {
  data: T | null;
  isLoading: boolean;
  error: string | null;
  redirectUrl: string | null;
  refetch: () => Promise<void>;
}

/**
 * Hook to fetch full content data from Umbraco CMS by key.
 * Only fetches when key is provided (non-null).
 *
 * @param key - The content key (ShortGuid), or null to skip fetching
 * @returns An object containing:
 *  - `data` – the fetched content, or null
 *  - `isLoading` – whether a fetch is in progress
 *  - `error` – error message if the fetch failed, or null
 *  - `refetch` – an async function (`() => Promise<void>`) that triggers a
 *    fresh load of the content associated with the current key (uses the same
 *    fetch logic as the hook's initial/automatic load). When key is null or
 *    undefined the call is effectively a no-op and resolves immediately.
 */
export function useContentByKey<T>(
  key: string | null
): UseContentByKeyResult<T> {
  const [data, setData] = useState<T | null>(null);
  // Loading from the first render when there is a key: the fetch only starts in
  // the effect, and a first render with neither data nor loading made the
  // product page render nothing, pulling the footer up into view (CLS).
  const [isLoading, setIsLoading] = useState(Boolean(key));
  const [error, setError] = useState<string | null>(null);
  const [redirectUrl, setRedirectUrl] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const load = useCallback(async () => {
    if (!key) return;

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setIsLoading(true);
    setError(null);
    setRedirectUrl(null);

    const result = await fetchContentByKey<T>(key, controller.signal);

    if (controller.signal.aborted) return;

    if (result.ok) {
      setData(result.data);
    } else {
      setError(result.error);
      setRedirectUrl(result.redirectUrl ?? null);
    }

    setIsLoading(false);
  }, [key]);

  useEffect(() => {
    if (key) {
      load();
    }

    return () => {
      abortRef.current?.abort();
    };
  }, [load, key]);

  return { data, isLoading, error, redirectUrl, refetch: load };
}
